import {Component, Input, OnInit, ChangeDetectionStrategy, HostBinding, HostListener, OnDestroy, ElementRef} from '@angular/core';
import {SortTableColumn, SortTableRow} from '../../../../shared/models/sort-table/types';
import {SortTableCellTextPillComponent} from '../cells/text-pill/sort-table-cell-text-pill.component';
import {SortDisplayNameCellComponent} from '../cells/display-name-cell/sort-display-name-cell.component';
import {SortTableCellTitleDescriptionComponent} from '../cells/title-description/sort-table-cell-title-description.component';
import {NgStyle, NgTemplateOutlet} from '@angular/common';
import {RouterLink} from '@angular/router';
import {SortTableCellTitleSharingInfoComponent} from '../cells/title-sharing-info/sort-table-cell-title-sharing-info.component';
import {SortTableCellStatusDotComponent} from '../cells/status-dot/sort-table-cell-status-dot.component';
import {SortTableCellKebabMenuComponent} from '../cells/kebab-menu/sort-table-cell-kebab-menu.component';
import {SortTableCellEmptyComponent} from '../cells/empty/sort-table-cell-empty.component';
import {SortTableCellIconComponent} from '../cells/icon/sort-table-cell-icon.component';
import {SortTableCellButtonsComponent} from '../cells/buttons/sort-table-cell-buttons.component';
import {SortTableCellUserLinkComponent} from '../cells/user-link/sort-table-cell-user-link.component';
import {SortTableCellToggleComponent} from '../cells/toggle/sort-table-cell-toggle.component';
import {RolePickerCellComponent} from '../cells/role-picker-cell/role-picker-cell.component';
import {SortTableCellSwatchComponent} from '../cells/swatch/sort-table-cell-swatch.component';
import {SortTableCellCurrentValueComponent} from '../cells/current-value/sort-table-cell-current-value.component';
import {SortTableCellTagsComponent} from '../cells/tags/sort-table-cell-tags.component';
import {SortTableCell} from '@src/shared/models/sort-table/cell-classes';
import {ViewportIntersectionService} from 'src/core/services/viewport-intersection.service';
import {Subscription} from 'rxjs';

@Component({
  selector: 'motn-sort-table-row',
  templateUrl: './sort-table-row.component.html',
  styleUrls: ['./sort-table-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SortTableCellTextPillComponent,
    SortDisplayNameCellComponent,
    SortTableCellTitleDescriptionComponent,
    NgStyle,
    NgTemplateOutlet,
    RouterLink,
    SortTableCellTitleSharingInfoComponent,
    SortTableCellStatusDotComponent,
    SortTableCellKebabMenuComponent,
    SortTableCellEmptyComponent,
    SortTableCellIconComponent,
    SortTableCellButtonsComponent,
    SortTableCellUserLinkComponent,
    SortTableCellToggleComponent,
    RolePickerCellComponent,
    SortTableCellSwatchComponent,
    SortTableCellCurrentValueComponent,
    SortTableCellTagsComponent,
  ],
})
export class SortTableRowComponent implements OnInit, OnDestroy {
  @Input() row: SortTableRow;
  @Input() columns: SortTableColumn[];

  isVisible: boolean = false;

  // Cell types that handle their own click events internally
  private readonly SELF_HANDLING_CELLS = new Set(['KebabMenu']);

  // Cell types that should not be wrapped in anchor (contains interactive elements or anchors)
  private readonly NON_WRAPPABLE_CELLS = new Set(['KebabMenu', 'Buttons', 'Toggle', 'RolePicker', 'CurrentValue', 'Tags']);

  private viewportSub: Subscription;

  constructor(
    private elementRef: ElementRef,
    private viewportService: ViewportIntersectionService
  ) {}


  ngOnInit(): void {
    if (this.row.onInitCallback && !this.row._initCallbackRan) {
      this.row.onInitCallback();
      this.row._initCallbackRan = true;

      // // This prevents the callback from being called again if
      // // the same row ends up rendered again (e.g. if user is
      // // moving between pages of results and comes back to a
      // // page he'd already seen before)
      // delete this.row.onInitCallback;
    }

    // Subscribe to viewport visibility changes using ViewportIntersectionService
    // Use 0px buffer for table rows (no preloading) since they scroll linearly
    // Unlike tile dashboard which needs large buffer for grid scrolling
    this.viewportSub = this.viewportService.observe$(
      this.elementRef.nativeElement,
      { rootMargin: '0px' }
    ).subscribe((isVisible) => {
      this.isVisible = isVisible;
    });
  }

  ngOnDestroy(): void {
    this.viewportService.unobserve(this.elementRef.nativeElement);
    this.viewportSub?.unsubscribe();
  }

  isCellClickable(cell: SortTableCell): boolean {
    // For CurrentValue cells, only clickable if there's a value
    if (cell.cellType === 'CurrentValue') {
      return !!(cell as any).hasValue && !!cell.clickCallback;
    }
    return cell.clickCallback && !this.SELF_HANDLING_CELLS.has(cell.cellType);
  }

  isCellInteractive(cell: SortTableCell): boolean {
    return this.NON_WRAPPABLE_CELLS.has(cell.cellType);
  }

  handleCellClick(cell: SortTableCell): void {
    // Skip TD-level clicks for cells that handle their own events
    if (this.SELF_HANDLING_CELLS.has(cell.cellType)) {
      return;
    }

    // Only execute callback if cell is actually clickable
    if (this.isCellClickable(cell)) {
      cell.clickCallback?.();
    }
  }
}
